import { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import '../../CSS/Usermaster.css';
import api from '../../api';
import { useRights } from "../RightsContext";

const User_MASTER_PATH = "System/Usermaster";
/* ---------------------------------------------------------------------- */
/* NOTE ON DATA MODEL                                                      */
/* RoleId and default_branch are now varchar(100) columns storing          */
/* comma-separated ids (e.g. "1,2,5"), so both Role and Branch use a       */
/* multi-select combobox. Ids are joined with ',' before saving and        */
/* split back into arrays when loading a user for edit/display.            */
/* Emp_Code / emp_id are intentionally never read or sent (per backend).   */
/* All writes use POST (not PUT/DELETE) since IIS blocks those verbs here. */
/* ---------------------------------------------------------------------- */

const AV_COLORS = ['#2563eb', '#0d9488', '#7c3aed', '#d97706', '#16a34a', '#dc2626', '#1a2a4a', '#0891b2'];

const EMPTY_FORM = {
  f_name: '',
  l_name: '',
  user_name: '',
  email_id: '',
  mobile_number: '',
  userType: '',
  password: '',
  roleIds: [],
  branchIds: [],
  empCode: '',  
  active: true,
};

function idsFrom(str) {
  if (str === null || str === undefined || str === '') return [];
  return String(str)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number);
}

/* ---------------------------------------------------------------------- */
/* Small inline icon components                                           */
/* ---------------------------------------------------------------------- */

const IcoUserGroup = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
);
const IcoPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>
);
const IcoSearch = () => (
  <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
);
const IcoEdit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
);
const IcoKey = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
);
const IcoLockClosed = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
);
const IcoLockOpen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 019.9-1" /></svg>
);
const IcoDelete = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
);
const IcoCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
);
const IcoLogoMark = () => (
  <svg viewBox="0 0 24 24"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" /></svg>
);
const IcoUserSingle = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>
);

/* ---------------------------------------------------------------------- */
/* Reusable multi-select combobox (used for both Role and Branch)          */
/* Visual style matches the RateDefinition ComboBox (search-as-you-type,   */
/* portal-rendered dropdown that escapes overflow:hidden) — but this one   */
/* keeps multiple options selected instead of a single value.             */
/* ---------------------------------------------------------------------- */

function MultiComboBox({ options, idKey, labelKey, selectedIds, onChange, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focIdx, setFocIdx] = useState(-1);
  const [dropStyle, setDropStyle] = useState({});
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  const opts = options.map((o) => ({ value: o[idKey], label: o[labelKey] }));
  const selectedOpts = selectedIds.map((id) => opts.find((o) => o.value === id)).filter(Boolean);
  const summary = selectedOpts.map((o) => o.label).join(', ');

  const filtered = filterOpts(query);

  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return <>{text.slice(0, idx)}<mark>{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>;
  };

  function updateDropPos() {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setDropStyle({ position: 'fixed', top: r.bottom + 4, left: r.left, width: r.width, zIndex: 99999 });
  }

  function openBox() {
    setOpen(true);
    setQuery('');
    setFocIdx(-1);
    setTimeout(() => {
      updateDropPos();
      inputRef.current && inputRef.current.focus();
    }, 0);
  }
  function closeBox() {
    setOpen(false);
    setQuery('');
    setFocIdx(-1);
  }
  function filterOpts(q) {
    return q.trim()
      ? opts.filter((o) => (o.label || '').toLowerCase().includes(q.toLowerCase()))
      : opts;
  }
  function handleQueryChange(e) {
    const val = e.target.value;
    setQuery(val);
    // Auto-highlight the first match as the user types, so Enter has
    // something to select without requiring an arrow-key press first.
    setFocIdx(filterOpts(val).length ? 0 : -1);
  }
  function toggleOpt(id) {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    // Reset the search text so the box visibly clears after a pick,
    // instead of leaving the typed query sitting there looking unselected.
    setQuery('');
    setFocIdx(-1);
    inputRef.current && inputRef.current.focus();
  }
  function clearAll(e) {
    e.stopPropagation();
    onChange([]);
  }

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeBox();
    }
    function onScroll() {
      updateDropPos();
    }
    function onResize() {
      updateDropPos();
    }
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (focIdx >= 0 && filtered[focIdx]) toggleOpt(filtered[focIdx].value); }
    else if (e.key === 'Escape') closeBox();
    else if (e.key === 'Backspace' && !query && selectedIds.length) onChange(selectedIds.slice(0, -1));
  }

  return (
    <div className="um-cb-ac" ref={wrapRef}>
      <div
        ref={triggerRef}
        className={'um-cb-ac-box' + (open ? ' open' : '') + (error ? ' um-err' : '')}
        onClick={openBox}
      >
        <input
          ref={inputRef}
          className="um-cb-ac-in"
          type="text"
          placeholder={placeholder}
          value={open ? query : summary}
          onChange={handleQueryChange}
          onKeyDown={handleKey}
          readOnly={!open}
          title={!open && summary ? summary : undefined}
          style={{ cursor: open ? 'text' : 'pointer' }}
        />
        {selectedOpts.length > 0 && <span className="um-cb-ac-count">{selectedOpts.length}</span>}
        {selectedOpts.length > 0 && (
          <span className="um-cb-ac-x on" onMouseDown={clearAll}>×</span>
        )}
        <span className="um-cb-ac-chev">▾</span>
      </div>

      {open && ReactDOM.createPortal(
        <div className="um-cb-ac-drop open" style={dropStyle}>
          {filtered.length === 0 ? (
            <div className="um-cb-ac-empty">No match</div>
          ) : (
            filtered.map((o, i) => {
              const on = selectedIds.includes(o.value);
              return (
                <div
                  key={o.value}
                  className={'um-cb-ac-opt' + (on ? ' sel' : '') + (i === focIdx ? ' foc' : '')}
                  onMouseDown={(e) => { e.preventDefault(); toggleOpt(o.value); }}
                >
                  <span className="um-cb-ac-chk">{on ? '✓' : ''}</span>
                  {highlight(o.label, query)}
                </div>
              );
            })
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function SingleComboBox({ options, idKey, labelKey, selectedId, onChange, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focIdx, setFocIdx] = useState(-1);
  const [dropStyle, setDropStyle] = useState({});
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const dropListRef = useRef(null);

  const opts = options.map((o) => ({ value: o[idKey], label: o[labelKey] }));
  const selected = opts.find((o) => o.value === selectedId) || null;

  const filtered = query.trim()
    ? opts.filter((o) => (o.label || '').toLowerCase().includes(query.toLowerCase()))
    : opts;

  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return <>{text.slice(0, idx)}<mark>{text.slice(idx, idx + q.length)}</mark>{text.slice(idx + q.length)}</>;
  };

  useEffect(() => {
    if (!open || focIdx < 0 || !dropListRef.current) return;
    const el = dropListRef.current.querySelector(`[data-idx="${focIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [focIdx, open]);

  function updateDropPos() {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setDropStyle({ position: 'fixed', top: r.bottom + 4, left: r.left, width: r.width, zIndex: 99999 });
  }

  function openBox() {
    setOpen(true);
    setQuery('');
    setFocIdx(-1);
    setTimeout(() => { updateDropPos(); inputRef.current && inputRef.current.focus(); }, 0);
  }
  function closeBox() {
    setOpen(false);
    setQuery('');
    setFocIdx(-1);
  }
  function pick(id) {
    onChange(id);
    closeBox();
  }

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) closeBox(); }
    function onScroll() { updateDropPos(); }
    function onResize() { updateDropPos(); }
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (focIdx >= 0 && filtered[focIdx]) pick(filtered[focIdx].value); else if (filtered.length === 1) pick(filtered[0].value); }
    else if (e.key === 'Escape') closeBox();
  }

  return (
    <div className="um-cb-ac" ref={wrapRef}>
      <div ref={triggerRef} className={'um-cb-ac-box' + (open ? ' open' : '') + (error ? ' um-err' : '')} onClick={openBox}>
        <input
          ref={inputRef}
          className="um-cb-ac-in"
          type="text"
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : (selected ? selected.label : '')}
          onChange={(e) => { setQuery(e.target.value); setFocIdx(-1); }}
          onKeyDown={handleKey}
          readOnly={!open}
          style={{ cursor: open ? 'text' : 'pointer' }}
        />
        {selected && !open && (
          <span className="um-cb-ac-x on" onMouseDown={(e) => { e.stopPropagation(); pick(null); }}>×</span>
        )}
        <span className="um-cb-ac-chev">▾</span>
      </div>

      {open && ReactDOM.createPortal(
        <div className="um-cb-ac-drop open" style={dropStyle} ref={dropListRef}>
          {filtered.length === 0 ? (
            <div className="um-cb-ac-empty">No results found</div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o.value}
                data-idx={i}
                className={'um-cb-ac-opt' + (o.value === selectedId ? ' sel' : '') + (i === focIdx ? ' foc' : '')}
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
              >
                {highlight(o.label, query)}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Helpers                                                                 */
/* ---------------------------------------------------------------------- */

function initials(f, l) {
  const a = (f || '').trim()[0] || '';
  const b = (l || '').trim()[0] || '';
  return (a + b).toUpperCase() || '?';
}
function avColor(id) {
  return AV_COLORS[id % AV_COLORS.length];
}
function fmtMobile(m) {
  return m && m.length === 10 ? m.slice(0, 5) + ' ' + m.slice(5) : m || '—';
}
function genPassword() {
  const s = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$';
  let p = '';
  for (let i = 0; i < 10; i++) p += s[Math.floor(Math.random() * s.length)];
  return p;
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleString();
}

/* ---------------------------------------------------------------------- */
/* Main component                                                         */
/* ---------------------------------------------------------------------- */

export default function UserMaster() {
  const rights = useRights(User_MASTER_PATH);
  const [users, setUsers] = useState([]);
  const [usernameTaken, setUsernameTaken] = useState(false); // true => "already exists" instead of "required"
  const [roles, setRoles] = useState([]);       // [{ RoleId, RoleName }]
  const [branches, setBranches] = useState([]); // [{ place_id, place_Name }]
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);

  const [editId, setEditId] = useState(null);
  const [resetId, setResetId] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const [showPass, setShowPass] = useState(false);
  const [showRpNew, setShowRpNew] = useState(false);

  const [rpNew, setRpNew] = useState('');
  const [rpConfirm, setRpConfirm] = useState('');

  const [toasts, setToasts] = useState([]);
  const nameInputRef = useRef(null);
  const toastId = useRef(1);

  const [userTypes, setUserTypes] = useState([]);

  /* ---------------- toast ---------------- */
  const toast = useCallback((msg, type) => {
    const id = toastId.current++;
    setToasts((t) => [...t, { id, msg, type, leaving: false }]);
    setTimeout(() => {
      setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 320);
    }, 2700);
  }, []);

  /* ---------------- initial data load ---------------- */
  async function loadUsers() {
    const res = await api.get('/api/UserMaster');
    setUsers(res.data || []);
  }
  async function loadDropdowns() {
    const [roleRes, branchRes, empRes,utRes ] = await Promise.all([
      api.get('/dropdown', { params: { mode: 'ro' } }),
      api.get('/dropdown', { params: { mode: 'plc' } }),
      api.get('/dropdown', { params: { mode: 'emp' } }),  
      api.get('/dropdown', { params: { mode: 'UTP' } })
    ]);
    setRoles(roleRes.data || []);
    setBranches(branchRes.data || []);
    setEmployees(empRes.data || []); 
    setUserTypes(utRes.data);  
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadUsers(), loadDropdowns()]);
      } catch (err) {
        console.error(err);
        toast('Failed to load data from server', 'err');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function roleNames(str) {
    const ids = idsFrom(str);
    if (!ids.length) return '—';
    return ids.map((id) => { const r = roles.find((x) => x.RoleId === id); return r ? r.RoleName : id; }).join(', ');
  }
  function branchNames(str) {
    const ids = idsFrom(str);
    if (!ids.length) return '—';
    return ids.map((id) => { const b = branches.find((x) => x.place_id === id); return b ? b.place_Name : id; }).join(', ');
  }

  /* ---------------- derived stats ---------------- */
  const stTotal = users.length;
  const stActive = users.filter((u) => u.status === 'Y').length;
  const stInactive = users.filter((u) => u.status !== 'Y').length;

  /* ---------------- filtered list ---------------- */
  const q = search.toLowerCase().trim();
  const filteredUsers = users.filter((u) => {
    if (filterRole && !idsFrom(u.RoleId).includes(+filterRole)) return false;
    if (filterStatus && (filterStatus === 'active' ? u.status !== 'Y' : u.status === 'Y')) return false;
    if (
      q &&
      !(
        (u.f_name || '').toLowerCase().includes(q) ||
        (u.l_name || '').toLowerCase().includes(q) ||
        (u.user_name || '').toLowerCase().includes(q) ||
        (u.email_id || '').toLowerCase().includes(q)
      )
    )
      return false;
    return true;
  });

  /* ---------------- pagination ---------------- */
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  useEffect(() => {
    // Search/filter changes can shrink the result set, so clamp the
    // current page back into range instead of showing an empty page.
    if (page > totalPages) setPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers.length]);
  const pageStart = (page - 1) * PAGE_SIZE;
  const pagedUsers = filteredUsers.slice(pageStart, pageStart + PAGE_SIZE);
  function goToPage(p) {
    setPage(Math.min(Math.max(1, p), totalPages));
  }
  // Reset to page 1 whenever the search text or a dropdown filter changes.
  function onSearchChange(v) { setSearch(v); setPage(1); }
  function onFilterRoleChange(v) { setFilterRole(v); setPage(1); }
  function onFilterStatusChange(v) { setFilterStatus(v); setPage(1); }

  /* ---------------- error helpers ---------------- */
  const clearErr = (id) => setErrors((e) => (e[id] ? { ...e, [id]: false } : e));

  /* ---------------- user modal ---------------- */
  function openUserModal(id) {
    const u = id ? users.find((x) => x.user_id === id) : null;
    setEditId(id || null);
    setForm(
      u
        ? {
            f_name: u.f_name || '',
            l_name: u.l_name || '',
            user_name: u.user_name || '',
            email_id: u.email_id || '',
            mobile_number: u.mobile_number || '',
            userType: u.User_Type || '',
            password: '',
            roleIds: idsFrom(u.roleId),
            branchIds: idsFrom(u.default_branch),
            empCode: u.Emp_Code ?? '',
            active: u.status === 'Y',
          }
        : { ...EMPTY_FORM }
    );
    setErrors({});
    setUsernameTaken(false);
    setShowPass(false);
    setUserModalOpen(true);
    setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 120);
  }
  function closeUserModal() {
    setUserModalOpen(false);
  }

  async function saveUser() {
    const f_name = form.f_name.trim();
    const l_name = form.l_name.trim();
    const user_name = form.user_name.trim().toLowerCase();
    const email_id = form.email_id.trim();
    const mobile_number = form.mobile_number.replace(/\D/g, '');
    const pass = form.password;

    let ok = true;
    const newErrors = {};
    if (!f_name) { newErrors['u-fname'] = true; ok = false; }
    if (!l_name) { newErrors['u-lname'] = true; ok = false; }
    if (!user_name) { newErrors['u-username'] = true; ok = false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email_id)) { newErrors['u-email'] = true; ok = false; }
    if (!form.userType) { newErrors['u-utype'] = true; ok = false; }
    if (!form.roleIds.length) { newErrors['u-role'] = true; ok = false; }
    if (!form.branchIds.length) { newErrors['u-branch'] = true; ok = false; }
    if (!editId && pass.length < 6) { newErrors['u-pass'] = true; ok = false; }

    // ---- Duplicate username check (client-side, against the already-loaded list) ----
    // Ignores the current record when editing, so saving a user without
    // changing their own username doesn't falsely flag as a duplicate.
    let duplicate = false;
    if (user_name) {
      duplicate = users.some(
        (u) => (u.user_name || '').trim().toLowerCase() === user_name && u.user_id !== editId
      );
      if (duplicate) {
        newErrors['u-username'] = true;
        ok = false;
      }
    }
    setUsernameTaken(duplicate);

    if (!ok) {
      setErrors(newErrors);
      toast(duplicate ? `Username "${user_name}" already exists` : 'Please fix the highlighted fields', 'warn');
      return;
    }

    const payload = {
      f_name,
      l_name,
      user_name,
      email_id,
      mobile_number,
      User_Type: form.userType,
      default_branch: form.branchIds.join(','), // varchar(100) comma-separated
      RoleId: form.roleIds.join(','),           // varchar(100) comma-separated
      emp_code: form.empCode || null,
      status: form.active ? 'Y' : 'N',
      finYearCode: Number(sessionStorage.getItem("finYearCode")) || null,
    };
    if (pass) payload.password = pass; // only send if set/changed

    setSaving(true);
    try {
      console.log("editId =", editId);
      console.log("Update URL =", `/api/UserMaster/${editId}`);
      console.log("Payload =", payload);
      if (editId) {
        await api.post(`/api/UserMaster/${editId}`, payload);
        toast('User updated — ' + f_name + ' ' + l_name, 'ok');
      } else {
        if (!pass) { setSaving(false); return; } // safety, already validated above
        // await api.post('/api/UserMaster', payload);
        await api({
            method: "post",
            url: "/api/UserMaster",
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        });
        toast('User created — ' + f_name + ' ' + l_name, 'ok');
      }
      await loadUsers();
      closeUserModal();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Save failed';
      // Backend is the source of truth for uniqueness (guards against a
      // race where someone else takes the username between load and save).
      if (/username already exists/i.test(msg)) {
        setUsernameTaken(true);
        setErrors((e) => ({ ...e, 'u-username': true }));
      }
      toast(msg, 'err');
    } finally {
      setSaving(false);
    }
  }

  async function delUser(id) {
    const u = users.find((x) => x.user_id === id);
    if (!u) return;
    if (!window.confirm('Delete user "' + u.f_name + ' ' + u.l_name + '" (@' + u.user_name + ')?')) return;
    try {
      // await api.post(`/api/UserMaster/${id}`);
      await api.post(`/api/UserMaster/${id}/delete`);
      toast('User deleted', 'info');
      await loadUsers();
    } catch (err) {
      toast(err?.response?.data?.message || 'Delete failed', 'err');
    }
  }

  async function toggleActiveUser(id) {
    const u = users.find((x) => x.user_id === id);
    if (!u) return;
    try {
      const res = await api.post(`/api/UserMaster/${id}/status`);
      const newStatus = res.data?.status;
      setUsers((list) => list.map((x) => (x.user_id === id ? { ...x, status: newStatus } : x)));
      toast(u.f_name + ' ' + (newStatus === 'Y' ? 'activated' : 'deactivated'), newStatus === 'Y' ? 'ok' : 'info');
    } catch (err) {
      toast(err?.response?.data?.message || 'Status update failed', 'err');
    }
  }

  /* ---------------- reset password modal ---------------- */
  function openReset(id) {
    setResetId(id);
    setRpNew('');
    setRpConfirm('');
    setShowRpNew(false);
    setErrors((e) => ({ ...e, 'rp-new': false, 'rp-confirm': false }));
    setResetModalOpen(true);
  }
  function closeResetModal() {
    setResetModalOpen(false);
  }
  async function saveReset() {
    let ok = true;
    const newErrors = {};
    if (rpNew.length < 6) { newErrors['rp-new'] = true; ok = false; }
    if (rpNew !== rpConfirm) { newErrors['rp-confirm'] = true; ok = false; }
    if (!ok) {
      setErrors((e) => ({ ...e, ...newErrors }));
      return;
    }
    const u = users.find((x) => x.user_id === resetId);
    try {
      await api.post(`/api/UserMaster/${resetId}/reset-password`, { NewPassword: rpNew });
      closeResetModal();
      toast('Password reset for ' + (u ? u.f_name : ''), 'ok');
    } catch (err) {
      toast(err?.response?.data?.message || 'Reset failed', 'err');
    }
  }

  const resetUser = resetId ? users.find((x) => x.user_id === resetId) : null;

  /* ---------------- render ---------------- */
  return (
    <div className="um-root">
      <div className="um-page">
        <div className="um-pg-row">
          <div className="um-pg-title">
            <h1>User Master</h1>
            <p>Create and manage users, assign a role &amp; branch, and control their status.</p>
          </div>
          {rights.add && (
          <button className="um-btn um-btn-primary um-btn-lg" onClick={() => openUserModal()}>
            <IcoPlus /> Add User
          </button>
          )}
        </div>

        <div className="um-stat-row">
          <div className="um-stat-box"><div className="um-sb-val">{stTotal}</div><div className="um-sb-lbl">Total Users</div></div>
          <div className="um-stat-box um-green"><div className="um-sb-val">{stActive}</div><div className="um-sb-lbl">Active</div></div>
          <div className="um-stat-box um-amber"><div className="um-sb-val">{stInactive}</div><div className="um-sb-lbl">Inactive</div></div>
        </div>

        <div className="um-card">
          <div className="um-band um-navy-band">
            <div className="um-band-icon"><IcoUserGroup /></div>
            <div><div className="um-band-title">Users</div></div>
            <div className="um-band-r"><span className="um-band-badge">{users.length} users</span></div>
          </div>

          <div className="um-toolbar">
            <div className="um-search">
              <IcoSearch />
              {/* <input
                placeholder="Search name / username / email…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              /> */}
          <div className="um-search">
            <IcoSearch />
            <input
              type="text"
              name="um-user-search"
              placeholder="Search name / username / email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
          </div>
            </div>
            <select className="um-tsel" value={filterRole} onChange={(e) => onFilterRoleChange(e.target.value)}>
              <option value="">All Roles</option>
              {roles.map((r) => (
                <option key={r.RoleId} value={r.RoleId}>{r.RoleName}</option>
              ))}
            </select>
            <select className="um-tsel" value={filterStatus} onChange={(e) => onFilterStatusChange(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="um-tbl-scroll">
            <table className="um-users">
              <thead>
                <tr>
                  <th>User</th><th>Contact</th><th>Role</th><th>Branch</th>
                  <th className="um-c">Status</th><th>Last Login</th><th className="um-c">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7}><div className="um-empty">Loading users…</div></td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="um-empty">
                        <div className="um-e-ico">👤</div>
                        <div style={{ fontWeight: 700, color: 'var(--slate)' }}>No users match</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedUsers.map((u) => (
                    <tr key={u.user_id}>
                      <td>
                        <div className="um-u-cell">
                          <div className="um-u-av" style={{ background: avColor(u.user_id) }}>{initials(u.f_name, u.l_name)}</div>
                          <div>
                            <div className="um-u-name">{u.f_name} {u.l_name}</div>
                            <div className="um-u-uname">@{u.user_name}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="um-u-contact">
                          {u.email_id}
                          <div className="um-um">+91 {fmtMobile(u.mobile_number)}</div>
                        </div>
                      </td>
                      <td><span className="um-role-badge" style={{ background: 'var(--blue-lt)', color: 'var(--blue)' }}>{roleNames(u.roleId)}</span></td>
                      {/* <td><span className="um-br-chip um-tooltip" data-tooltip={branchNames(u.default_branch)}>{branchNames(u.default_branch)}</span></td> */}
                      <td><span className="um-br-chip" title={branchNames(u.default_branch)}>{branchNames(u.default_branch)}</span></td>
                      <td className="um-c">
                        <span className={'um-pill ' + (u.status === 'Y' ? 'um-pill-active' : 'um-pill-inactive')}>
                          <span className="um-pill-dot"></span>{u.status === 'Y' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td><span className="um-u-login">{fmtDate(u.last_login)}</span></td>
                      <td className="um-c">
                        <div className="um-row-acts">
                          {rights.edit && (<button className="um-ic-btn um-ic-edit" title="Edit" onClick={() => openUserModal(u.user_id)}><IcoEdit /></button>)}
                          <button className="um-ic-btn um-ic-key" title="Reset password" onClick={() => openReset(u.user_id)}><IcoKey /></button>
                          <button
                            className="um-ic-btn um-ic-lock"
                            title={u.status === 'Y' ? 'Deactivate' : 'Activate'}
                            onClick={() => toggleActiveUser(u.user_id)}
                          >
                            {u.status === 'Y' ? <IcoLockClosed /> : <IcoLockOpen />}
                          </button>
                          {rights.delete && (<button className="um-ic-btn um-ic-del" title="Delete" onClick={() => delUser(u.user_id)}><IcoDelete /></button>)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && filteredUsers.length > 0 && (
            <div className="um-pagination">
              <div className="um-pg-info">
                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length}
              </div>
              <div className="um-pg-controls">
                <button
                  className="um-ic-btn"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  title="Previous page"
                >
                  ‹
                </button>
                {(() => {
                  const nums = Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1);
                  const out = [];
                  nums.forEach((p, i) => {
                    if (i > 0 && p - nums[i - 1] > 1) out.push(<span key={'gap' + p} className="um-pg-gap">…</span>);
                    out.push(
                      <button
                        key={p}
                        className={'um-pg-num' + (p === page ? ' active' : '')}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    );
                  });
                  return out;
                })()}
                <button
                  className="um-ic-btn"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  title="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- Add / Edit user modal ---------------- */}
      <div className={'um-modal-overlay' + (userModalOpen ? ' um-show' : '')} onMouseDown={(e) => e.target === e.currentTarget && closeUserModal()}>
        <div className="um-modal">
          <div className="um-modal-head">
            <div className="um-mh-ico"><IcoUserSingle /></div>
            <div>
              <div className="um-mh-title">{editId ? 'Edit User' : 'Add User'}</div>
              <div className="um-mh-sub">{editId ? 'Update details & role' : 'Create a user and assign a role'}</div>
            </div>
            <button className="um-modal-x" onClick={closeUserModal}>✕</button>
          </div>
          <div className="um-modal-body">
            <div className="um-sec-lbl">User Details</div>
            <div className="um-g2">
              <div className="um-field">
                <label>First Name <span className="um-req">*</span></label>
                <input
                  ref={nameInputRef}
                  type="text"
                  className={errors['u-fname'] ? 'um-err' : ''}
                  placeholder="e.g. Ravi"
                  value={form.f_name}
                  onChange={(e) => { setForm((f) => ({ ...f, f_name: e.target.value })); clearErr('u-fname'); }}
                />
                <div className={'um-field-err' + (errors['u-fname'] ? ' um-show' : '')}>First name is required</div>
              </div>
              <div className="um-field">
                <label>Last Name <span className="um-req">*</span></label>
                <input
                  type="text"
                  className={errors['u-lname'] ? 'um-err' : ''}
                  placeholder="e.g. Kumar"
                  value={form.l_name}
                  onChange={(e) => { setForm((f) => ({ ...f, l_name: e.target.value })); clearErr('u-lname'); }}
                />
                <div className={'um-field-err' + (errors['u-lname'] ? ' um-show' : '')}>Last name is required</div>
              </div>
            </div>
            <div className="um-g2">
              <div className="um-field">
                <label>Username <span className="um-req">*</span></label>
                <input
                  type="text"
                  className={'um-mono' + (errors['u-username'] ? ' um-err' : '')}
                  placeholder="e.g. ravi.kumar"
                  value={form.user_name}
                  onChange={(e) => { setForm((f) => ({ ...f, user_name: e.target.value })); clearErr('u-username'); setUsernameTaken(false); }}
                />
                <div className={'um-field-err' + (errors['u-username'] ? ' um-show' : '')}>
                  {usernameTaken ? `Username "${form.user_name.trim()}" already exists` : 'Username is required'}
                </div>
              </div>
              <div className="um-field">
                <label>Email <span className="um-req">*</span></label>
                <input
                  type="email"
                  className={errors['u-email'] ? 'um-err' : ''}
                  placeholder="user@saamitradestar.com"
                  value={form.email_id}
                  onChange={(e) => { setForm((f) => ({ ...f, email_id: e.target.value })); clearErr('u-email'); }}
                />
                <div className={'um-field-err' + (errors['u-email'] ? ' um-show' : '')}>Enter a valid email</div>
              </div>
            </div>
            <div className="um-field" style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: '0 0 55%' }}>
                <label>Mobile</label>
                <div className="um-mobile-wrap">
                  <span className="um-mobile-cc">+91</span>
                  <input
                    type="tel"
                    className="um-mono"
                    maxLength={11}
                    placeholder="10-digit"
                    value={form.mobile_number}
                    onChange={(e) => setForm((f) => ({ ...f, mobile_number: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ flex: '0 0 45%' }}>
                <label>User Type <span className="um-req">*</span></label>
                <select
                  className={errors['u-utype'] ? 'um-err' : ''}
                  value={form.userType}
                  onChange={(e) => { setForm((f) => ({ ...f, userType: e.target.value })); clearErr('u-utype'); }}
                >
                  <option value="">Select…</option>
                  {userTypes.map((t) => (
                    <option key={t.user_type_id} value={t.user_Type_Val}>{t.user_type_name}</option>
                  ))}
                </select>
                <div className={'um-field-err' + (errors['u-utype'] ? ' um-show' : '')}>User type is required</div>
              </div>
            </div>

            <div className="um-sec-lbl">Role &amp; Branch</div>
            <div className="um-field">
              <label>
                Role <span className="um-req">*</span>{' '}
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--muted)' }}>— multiple selection</span>
              </label>
              <MultiComboBox
                options={roles}
                idKey="RoleId"
                labelKey="RoleName"
                selectedIds={form.roleIds}
                onChange={(ids) => { setForm((f) => ({ ...f, roleIds: ids })); clearErr('u-role'); }}
                placeholder="Select roles…"
                searchPlaceholder="Search role…"
                error={errors['u-role']}
              />
              <div className={'um-field-err' + (errors['u-role'] ? ' um-show' : '')}>Select at least one role</div>
            </div>

            <div className="um-field" style={{ marginTop: 13 }}>
              <label>
                Branch <span className="um-req">*</span>{' '}
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--muted)' }}>— multiple selection</span>
              </label>
              <MultiComboBox
                options={branches}
                idKey="place_id"
                labelKey="place_Name"
                selectedIds={form.branchIds}
                onChange={(ids) => { setForm((f) => ({ ...f, branchIds: ids })); clearErr('u-branch'); }}
                placeholder="Select branches…"
                searchPlaceholder="Search branch…"
                error={errors['u-branch']}
              />
              <div className={'um-field-err' + (errors['u-branch'] ? ' um-show' : '')}>Select at least one branch</div>
            </div>

            <div className="um-field" style={{ marginTop: 13 }}>
              <label>
                Employee Name{' '}
                <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--muted)' }}>
                  — link to employee record
                </span>
              </label>
              <SingleComboBox
                options={employees}
                idKey="Emp_code"
                labelKey="Emp_Name"
                selectedId={form.empCode || null}
                onChange={(id) => {
                  setForm((f) => ({ ...f, empCode: id === null ? null : Number(id) }));
                  clearErr('u-emp');
                }}
                placeholder="Type to search employee…"
                error={errors['u-emp']}
              />
            </div>

            <div className="um-sec-lbl">Security</div>
            {!editId && (
              <div className="um-field">
                <label>Password <span className="um-req">*</span></label>
                <div className="um-pw-wrap">
                  <input
                    type={showPass ? 'text' : 'password'}
                    className={'um-mono' + (errors['u-pass'] ? ' um-err' : '')}
                    placeholder="Set a password"
                    value={form.password}
                    onChange={(e) => { setForm((f) => ({ ...f, password: e.target.value })); clearErr('u-pass'); }}
                  />
                  <button className="um-pw-eye" type="button" onClick={() => setShowPass((s) => !s)}>{showPass ? '🙈' : '👁'}</button>
                  {/* Generate button removed — it silently overwrote whatever
                      password the admin had already typed, and Save then
                      stored that random value instead of the intended one.
                      If a random password is needed, type it manually or
                      re-enable this after adding a clear confirm/preview step.
                  <button
                    className="um-btn um-btn-ghost um-btn-sm"
                    type="button"
                    onClick={() => { setForm((f) => ({ ...f, password: genPassword() })); setShowPass(true); clearErr('u-pass'); toast('Password generated', 'info'); }}
                  >
                    Generate
                  </button>
                  */}
                </div>
                <div className={'um-field-err' + (errors['u-pass'] ? ' um-show' : '')}>Minimum 6 characters</div>
              </div>
            )}
            {editId && (
              <div className="um-note">Use the <b>Reset Password</b> action on the user row to change an existing user's password.</div>
            )}

            <div className="um-tog-row" style={{ marginTop: 13 }}>
              <div>
                <div className="um-tog-name">Active</div>
                <div className="um-tog-desc">User can sign in and access the system</div>
              </div>
              <label className="um-switch">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                <span className="um-slider"></span>
              </label>
            </div>
          </div>
          <div className="um-modal-foot">
            <button className="um-btn um-btn-ghost" onClick={closeUserModal} disabled={saving}>Cancel</button>
            {rights.add && (
              <button className="um-btn um-btn-green" onClick={saveUser} disabled={saving}>
                <IcoCheck /> {saving ? 'Saving…' : 'Save User'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- Reset password modal ---------------- */}
      <div className={'um-modal-overlay' + (resetModalOpen ? ' um-show' : '')} onMouseDown={(e) => e.target === e.currentTarget && closeResetModal()}>
        <div className="um-modal um-sm">
          <div className="um-modal-head">
            <div className="um-mh-ico"><IcoKey /></div>
            <div>
              <div className="um-mh-title">Reset Password</div>
              <div className="um-mh-sub">{resetUser ? resetUser.f_name + ' ' + resetUser.l_name + ' (@' + resetUser.user_name + ')' : '—'}</div>
            </div>
            <button className="um-modal-x" onClick={closeResetModal}>✕</button>
          </div>
          <div className="um-modal-body">
            <div className="um-field">
              <label>New Password <span className="um-req">*</span></label>
              <div className="um-pw-wrap">
                <input
                  type={showRpNew ? 'text' : 'password'}
                  className={'um-mono' + (errors['rp-new'] ? ' um-err' : '')}
                  placeholder="New password"
                  value={rpNew}
                  onChange={(e) => { setRpNew(e.target.value); clearErr('rp-new'); }}
                />
                <button className="um-pw-eye" type="button" onClick={() => setShowRpNew((s) => !s)}>{showRpNew ? '🙈' : '👁'}</button>
                {/* Generate button removed — same reason as the Add User
                    password field: it silently overwrote the admin's
                    typed password before Reset was clicked.
                <button
                  className="um-btn um-btn-ghost um-btn-sm"
                  type="button"
                  onClick={() => {
                    const p = genPassword();
                    setRpNew(p);
                    setRpConfirm(p);
                    setShowRpNew(true);
                    clearErr('rp-new');
                    clearErr('rp-confirm');
                    toast('Password generated', 'info');
                  }}
                >
                  Generate
                </button>
                */}
              </div>
              <div className={'um-field-err' + (errors['rp-new'] ? ' um-show' : '')}>Minimum 6 characters</div>
            </div>
            <div className="um-field">
              <label>Confirm Password <span className="um-req">*</span></label>
              <input
                type={showRpNew ? 'text' : 'password'}
                className={'um-mono' + (errors['rp-confirm'] ? ' um-err' : '')}
                placeholder="Re-enter password"
                value={rpConfirm}
                onChange={(e) => { setRpConfirm(e.target.value); clearErr('rp-confirm'); }}
              />
              <div className={'um-field-err' + (errors['rp-confirm'] ? ' um-show' : '')}>Passwords do not match</div>
            </div>
          </div>
          <div className="um-modal-foot">
            <button className="um-btn um-btn-ghost" onClick={closeResetModal}>Cancel</button>
            <button className="um-btn um-btn-green" onClick={saveReset}>Reset Password</button>
          </div>
        </div>
      </div>

      {/* ---------------- Toasts ---------------- */}
      <div className="um-toast-wrap">
        {toasts.map((t) => {
          const icons = { ok: '✓', err: '✕', warn: '⚠', info: 'ℹ' };
          return (
            <div key={t.id} className={'um-toast um-' + (t.type || 'info') + (t.leaving ? ' um-leaving' : '')}>
              <span style={{ fontSize: 15 }}>{icons[t.type] || 'ℹ'}</span>{t.msg}
            </div>
          );
        })}
      </div>
    </div>
  );
}